import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/auth.css';
import { logSaasEvent } from '../utils/saasAnalytics';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [loading, setLoading] = useState(false);
    const [trialDays, setTrialDays] = useState(null);
    const [mode, setMode] = useState('login'); // 'login', 'forgot', 'reset'
    const [resetEmail, setResetEmail] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTrialDays = async () => {
            try {
                const res = await fetch('https://menu.searchisend.in/apimenu/landing_actions.php?action=fetch_pricing_settings');
                const data = await res.json();
                if (data.success && data.pricing && data.pricing.trial_days !== undefined) {
                    setTrialDays(data.pricing.trial_days);
                }
            } catch (err) {
                console.error("Error fetching trial days:", err);
            }
        };
        fetchTrialDays();
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/saas_auth.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'login', username, password })
            });
            const data = await res.json();
            
            if (data.success) {
                logSaasEvent('login_success');
                localStorage.setItem('saas_token', data.token);
                localStorage.setItem('saas_user', JSON.stringify(data.user));
                navigate('/manage_v2_secure_auth/');
            } else {
                if (data.chances_left !== undefined && data.chances_left > 0) {
                    setError(`${data.message} ${data.chances_left} attempts remaining.`);
                } else {
                    setError(data.message || 'Login failed.');
                }
            }
        } catch (err) {
            setError('Network error. Could not connect to authentication server.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMsg('');
        setLoading(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/saas_auth.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'forgot_password', email: resetEmail })
            });
            const data = await res.json();
            if (data.success) {
                setSuccessMsg(data.message);
                setMode('reset');
            } else {
                setError(data.message || 'Failed to send reset link.');
            }
        } catch (err) {
            setError('Network error.');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMsg('');
        setLoading(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/saas_auth.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'reset_password', email: resetEmail, code: resetCode, new_password: newPassword })
            });
            const data = await res.json();
            if (data.success) {
                setSuccessMsg(data.message);
                setMode('login');
                setResetEmail('');
                setResetCode('');
                setNewPassword('');
            } else {
                setError(data.message || 'Failed to reset password.');
            }
        } catch (err) {
            setError('Network error.');
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-header">
                    <h2>{mode === 'login' ? 'Welcome Back' : mode === 'forgot' ? 'Reset Password' : 'New Password'}</h2>
                    <p>{mode === 'login' ? 'Log in to manage your premium digital menu.' : mode === 'forgot' ? 'Enter your email to receive a 6-digit code.' : 'Enter the code sent to your email and your new password.'}</p>
                </div>

                {error && <div className="auth-error">{error}</div>}
                {successMsg && <div className="auth-error" style={{backgroundColor: '#d4edda', color: '#155724', borderLeftColor: '#28a745'}}>{successMsg}</div>}

                {mode === 'login' && (
                <form onSubmit={handleLogin}>
                    <div className="form-group">
                        <label>Username or Email</label>
                        <input 
                            type="text" 
                            required
                            className="form-control"
                            value={username} 
                            onChange={(e) => setUsername(e.target.value)} 
                            placeholder="e.g., sharmadhaba or you@example.com" 
                        />
                    </div>
                    <div className="form-group" style={{ marginBottom: '25px' }}>
                        <label>Password</label>
                        <input 
                            type="password" 
                            required
                            className="form-control"
                            value={password} 
                            onChange={(e) => setPassword(e.target.value)} 
                            placeholder="••••••••" 
                        />
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-15px', marginBottom: '20px' }}>
                        <button type="button" onClick={() => { setMode('forgot'); setError(''); setSuccessMsg(''); }} style={{ background: 'none', border: 'none', color: '#ea580c', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}>Forgot Password?</button>
                    </div>
                    
                    <button type="submit" disabled={loading} className="auth-btn">
                        {loading ? 'Logging in...' : 'Login securely'}
                    </button>
                </form>
                )}

                {mode === 'forgot' && (
                <form onSubmit={handleForgotPassword}>
                    <div className="form-group">
                        <label>Registered Email</label>
                        <input type="email" required className="form-control" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="you@example.com" />
                    </div>
                    <button type="submit" disabled={loading} className="auth-btn">
                        {loading ? 'Sending...' : 'Send Reset Code'}
                    </button>
                    <div style={{ textAlign: 'center', marginTop: '15px' }}>
                        <button type="button" onClick={() => { setMode('login'); setError(''); setSuccessMsg(''); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.9rem' }}>Back to Login</button>
                    </div>
                </form>
                )}

                {mode === 'reset' && (
                <form onSubmit={handleResetPassword}>
                    <div className="form-group">
                        <label>6-Digit Code</label>
                        <input type="text" required className="form-control" value={resetCode} onChange={(e) => setResetCode(e.target.value)} placeholder="123456" />
                    </div>
                    <div className="form-group" style={{ marginBottom: '25px' }}>
                        <label>New Password</label>
                        <input type="password" required className="form-control" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" />
                    </div>
                    <button type="submit" disabled={loading} className="auth-btn">
                        {loading ? 'Resetting...' : 'Update Password'}
                    </button>
                    <div style={{ textAlign: 'center', marginTop: '15px' }}>
                        <button type="button" onClick={() => { setMode('login'); setError(''); setSuccessMsg(''); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.9rem' }}>Back to Login</button>
                    </div>
                </form>
                )}

                {mode === 'login' && (
                <div className="auth-footer">
                    <p>New here? <Link to="/signup" className="auth-link">{trialDays && trialDays > 0 ? `Start ${trialDays}-Day Free Trial` : 'Get Started'}</Link></p>
                </div>
                )}
            </div>
        </div>
    );
};

export default Login;

